import React from 'react';
import { DisciplineState, DisciplineThresholdsBundle, Intent } from '../../../types/discipline';
import { verdictLine } from '../../../utils/disciplineVerdict';
import { contractCode, ExplainedLine, explainedLines, fmtSigned, ruleProximity, ruleRows } from '../../../utils/positionLedger';
import { PremiumGlyph, RuleGlyph, StrikeGlyph } from '../../common/Glyphs';
import { isNonTradingDay } from '../../../utils/marketHours';
import PositionCockpit from './PositionCockpit';
import PositionDrawer from './PositionDrawer';
import LatestPriceChip from '../../common/LatestPriceChip';
import { LatestPositionQuote } from '../../../types/latestPrice';
import { LedgerPosition } from '../../../hooks/usePortfolioKPILoader';

const sign = (v: number | null | undefined) => (v == null ? '' : v > 0 ? 'ck-gain' : v < 0 ? 'ck-loss' : 'ck-zero');
/** The latest-price P&L is secondary: shown outside the regular session, or when
 *  the latest price has moved away from the 15-min Price. */
const showLatest = (l: LatestPositionQuote | null | undefined): l is LatestPositionQuote =>
  !!l && l.latest_today_pnl != null && (l.session !== 'regular' || !!l.differs_from_price);
const fmtDate = (iso?: string | null) => (iso ? new Date(iso + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—');
const money = (v: number | null | undefined) => (v == null ? '—' : fmtSigned(v));

/** Total, explained (glossary pnl_explained, level 1): Shares · Premium income ·
 *  Cost of capped upside · Dividends — server figures that add up to Total.
 *  The ticket shows level 2 (time decay, volatility, price effect). */
const Split: React.FC<{ rows: ExplainedLine[] }> = ({ rows }) => {
  const top = rows.filter((r) => !r.sub);
  const max = Math.max(...top.map((r) => Math.abs(r.value ?? 0)), 1);
  return (
    <div className="pl-split" aria-label={top.map((r) => `${r.label} ${money(r.value)}`).join(', ')}>
      {top.map((r) => {
        const v = r.value;
        const w = v == null || v === 0 ? 0 : Math.max(1, (Math.abs(v) / max) * 22);
        return (
          <div key={r.key} className="pl-sr" title={r.label}>
            <span>{r.label}</span>
            <span className="pl-st">
              <span className="z" />
              {w > 0 && <i className={(v as number) >= 0 ? 'is-gain' : 'is-loss'} style={{ left: (v as number) >= 0 ? 23 : 23 - w, width: w }} />}
            </span>
            <b className={sign(v)}>{money(v)}</b>
          </div>
        );
      })}
    </div>
  );
};

interface RowProps {
  position: LedgerPosition;
  discipline: DisciplineState | null | undefined;
  /** Server latest P&L on the latest delayed price (default memo compares it by reference). */
  latest: LatestPositionQuote | null | undefined;
  deltaLine: number | null;
  /** Profit target for this position's intent, 0–100 (premium glyph hairline). */
  profitLine: number | null;
  open: boolean;
  onToggle: (id: string) => void;
  onAction: (position: any, action: string, discipline?: DisciplineState | null) => void;
}

const LedgerRow: React.FC<RowProps> = React.memo(({ position: p, discipline: d, latest, deltaLine, profitLine, open, onToggle, onAction }) => {
  const isCsp = p.positionType === 'cash_secured_put';
  const split = explainedLines(p, isCsp);
  const v = d ? verdictLine(d) : null;
  const dayLabel = isNonTradingDay() ? 'last session' : 'today';
  // Simple: what to do, and which rule said so. The ticket has the numbers.
  const rows = ruleRows(d);
  const prox = ruleProximity(d);
  const otm = p.currentPrice != null && p.currentPrice > 0 && p.strikePrice ? ((isCsp ? p.currentPrice - p.strikePrice : p.strikePrice - p.currentPrice) / p.currentPrice) * 100 : null;
  const othersFired = d?.fired_rule ? rows.filter((r) => r.fired && !r.governs).length : 0;
  const ruleTitle = d?.fired_rule && d.action
    ? d.action
    : d === null ? "Couldn't load your rules"
    : d === undefined ? 'Checking your rules…'
    : v?.tone === 'quiet' ? 'Hold'
    : v?.headline ?? 'Hold';
  const ruleSub = d?.fired_rule
    ? `${d.fired_rule.name.toLowerCase()}${othersFired ? ` +${othersFired} more` : ''}`
    : v?.tone === 'quiet' ? 'no rule fired' : '';
  const primary = d?.action === 'Roll' ? 'Review roll' : d?.action && d.action.startsWith('Close') ? 'Review close' : d?.action === 'Let Assign' ? 'Review assignment' : 'Manage';
  // Delta shown as its size (a put's delta is negative by convention).
  const delta = p.currentDelta != null ? Math.abs(p.currentDelta) : null;
  const entryDelta = p.entryDelta != null ? Math.abs(p.entryDelta) : null;
  const pct = (x: number) => `${Math.max(0, Math.min(100, x * 100))}%`;
  const captured = p.premiumCapturedPct ?? null;

  return (
    <div
      className={`pl-row ${open ? 'is-open' : ''}`}
      role="row"
      onClick={(e) => {
        if ((e.target as HTMLElement).closest('button:not(.pl-toggle)')) return;
        onToggle(p.id);
      }}
    >
      <div className="pl-c pl-pos" role="cell">
        <button type="button" className="pl-toggle" aria-haspopup="dialog" aria-expanded={open} onClick={(e) => { e.stopPropagation(); onToggle(p.id); }}>
          <svg className="pl-chev" viewBox="0 0 16 16" aria-hidden="true"><path d="m6 4 4 4-4 4" /></svg>
          <span className="pl-tk">{p.ticker}</span>
          <span className={`pl-badge ${isCsp ? 'is-csp' : 'is-cc'}`}>{isCsp ? 'CSP' : 'CC'}</span>
        </button>
        <span className="pl-s">{p.currentPrice != null ? `$${p.currentPrice.toFixed(2)}` : '—'}</span>
      </div>
      <div className="pl-c" role="cell">
        <span className="pl-p pl-k">${p.strikePrice} {isCsp ? 'put' : 'call'}<StrikeGlyph otmPct={otm} /></span>
        <span className="pl-s">
          {fmtDate(p.expiryDate)} · {p.daysToExpiration ?? '—'}d · {p.numContracts || 1}×
          {p.earningsInside && <span className="pl-warn"> · earnings {fmtDate(p.nextEarningsDate)}</span>}
        </span>
      </div>
      <div className="pl-c pl-rule" role="cell">
        <span className="pl-p"><RuleGlyph progress={prox.progress} fired={prox.fired} title={d?.fired_rule ? `${d.fired_rule.name} fired` : prox.nearest ? `Nearest line: ${prox.nearest}` : undefined} /><b>{ruleTitle}</b></span>
        <span className="pl-s">{ruleSub}</span>
      </div>
      <div className="pl-c pl-r pl-today" role="cell">
        <span className={`pl-p ${sign(p.todayPnl)}`} title={`P&L ${dayLabel}`}>{money(p.todayPnl)}</span>
        <span className="pl-s">{dayLabel}</span>
        {showLatest(latest) && (
          <LatestPriceChip
            className="pl-latest"
            session={latest.bar_session}
            barTime={latest.bar_time}
            delayedMinutes={latest.delayed_minutes}
            pnl={latest.latest_today_pnl}
            compact
          />
        )}
      </div>
      <div className="pl-c pl-r" role="cell">
        <span className={`pl-p ${sign(p.totalPnl)}`}>{money(p.totalPnl)}</span>
        <span className="pl-s" title="Campaign P&L: everything made on these shares since you first bought them (or were assigned)">
          {(p.legCount ?? 1) > 1 || p.isWheel ? <>campaign {money(p.campaignPnl)}</> : 'this leg'}
        </span>
      </div>
      <div className="pl-c pl-hide-m" role="cell">{split ? <Split rows={split} /> : <span className="pl-s">—</span>}</div>
      <div className="pl-c pl-r" role="cell">
        <span className={`pl-p ${sign(p.thetaPerDay)}`}>{money(p.thetaPerDay)}</span>
        <span className="pl-s">per day</span>
      </div>
      <div className="pl-c pl-hide-m" role="cell">
        <span className="pl-p pl-k" title="Premium captured: the drop in the option's price as a share of what you sold it for (% of max profit)">
          <PremiumGlyph captured={captured} line={profitLine != null ? profitLine / 100 : null} />
          {captured != null ? `${Math.round(captured * 100)}%` : '—'}
        </span>
      </div>
      <div className="pl-c pl-hide-m" role="cell">
        <span className="pl-p">
          {entryDelta != null && <span className="pl-of">{entryDelta.toFixed(2)} → </span>}
          {delta != null ? delta.toFixed(2) : '—'}
        </span>
        <span className="pl-track" title={deltaLine != null ? `Tick = your defend line at Δ ${deltaLine.toFixed(2)}` : undefined}>
          {delta != null && <i style={{ width: pct(delta) }} />}
          {deltaLine != null && <span className="ln" style={{ left: pct(deltaLine) }} />}
          {entryDelta != null && <span className="e" style={{ left: pct(entryDelta) }} />}
          {delta != null && <span className="n" style={{ left: pct(delta) }} />}
        </span>
      </div>
      <div className="pl-c pl-act-c" role="cell">
        <button type="button" className="ck-btn is-primary" onClick={(e) => { e.stopPropagation(); onAction(p, 'resolve', d ?? null); }}>{primary}</button>
      </div>
    </div>
  );
});

/** A leg closed, assigned, expired or rolled today: it still counts in Today's
 *  P&L (the header adds it), so it has a row until the day ends. */
const ClosedTodayRow: React.FC<{ position: LedgerPosition }> = ({ position: p }) => {
  const isCsp = p.positionType === 'cash_secured_put';
  const what = p.status === 'rolled' ? 'rolled today' : p.status === 'assigned' ? 'assigned today' : p.status === 'expired' ? 'expired today' : 'closed today';
  return (
    <div className="pl-row is-closed-today" role="row">
      <div className="pl-c pl-pos" role="cell">
        <span className="pl-tk">{p.ticker}</span>
        <span className={`pl-badge ${isCsp ? 'is-csp' : 'is-cc'}`}>{isCsp ? 'CSP' : 'CC'}</span>
      </div>
      <div className="pl-c" role="cell"><span className="pl-s">{contractCode(p.expiryDate, p.strikePrice, isCsp)} · {what}</span></div>
      <div className="pl-c pl-rule" role="cell" />
      <div className="pl-c pl-r pl-today" role="cell">
        <span className={`pl-p ${sign(p.todayPnl)}`}>{money(p.todayPnl)}</span>
        <span className="pl-s">{isNonTradingDay() ? 'last session' : 'today'}</span>
      </div>
    </div>
  );
};

/** Drawer header quote: last, day P/L, and the mark's date when it isn't today's. */
const DrawerQuote: React.FC<{ position: LedgerPosition; latest: LatestPositionQuote | null | undefined }> = ({ position: p, latest }) => {
  const stale = p.optionIsStale && p.optionPriceLastUpdated
    ? new Date(p.optionPriceLastUpdated).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : null;
  return (
    <>
      <b>{p.currentPrice != null ? p.currentPrice.toFixed(2) : '—'}</b>
      {p.todayPnl != null && <span className={`pdr-day ${sign(p.todayPnl)}`} title={`P&L ${isNonTradingDay() ? 'last session' : 'today'}`}>{fmtSigned(p.todayPnl).replace('$', '')}</span>}
      {stale && <span className="pdr-stale" title="Option marks are from this date">mark {stale}</span>}
      {latest?.latest_price != null && (
        <LatestPriceChip
          className="pdr-latest"
          session={latest.bar_session}
          barTime={latest.bar_time}
          delayedMinutes={latest.delayed_minutes}
          price={latest.latest_price}
        />
      )}
    </>
  );
};

interface Props {
  positions: LedgerPosition[];
  closedToday?: LedgerPosition[];
  disciplines: Map<string, DisciplineState | null>;
  latestPnl?: Map<string, LatestPositionQuote>;
  thresholdsBundle: DisciplineThresholdsBundle | null;
  chartDataCache: Record<string, { results: any[] }>;
  openId: string | null;
  setOpenId: (id: string | null) => void;
  onAction: (position: any, action: string, discipline?: DisciplineState | null) => void;
  onDelete: (positionId: string) => void;
  onIntentChanged: (positionId: string, intent: Intent, discipline: DisciplineState | null) => void;
}

/**
 * Positions ledger: one row per position, sorted by urgency by the page, plus
 * the legs that ended today (their Today's P&L is in the header). Clicking a
 * row opens its ticket in the right-side drawer; ↑/↓ step through the list.
 */
const PositionLedger: React.FC<Props> = ({
  positions, closedToday = [], disciplines, latestPnl, thresholdsBundle, chartDataCache, openId, setOpenId, onAction, onDelete, onIntentChanged,
}) => {
  const toggle = React.useCallback((id: string) => setOpenId(openId === id ? null : id), [openId, setOpenId]);
  const openIndex = openId ? positions.findIndex((p) => p.id === openId) : -1;
  const openPos = openIndex >= 0 ? positions[openIndex] : null;

  return (
    <div className="pl" role="table" aria-label="Positions">
      <div className="pl-head" role="row">
        <span role="columnheader">Position</span>
        <span role="columnheader">Contract</span>
        <span role="columnheader">Your rule</span>
        <span role="columnheader" className="pl-r">{isNonTradingDay() ? 'Last session' : 'Today'}</span>
        <span role="columnheader" className="pl-r" title="Total P&L: this leg — the shares since this option was sold, the option, dividends. Campaign P&L underneath for rolled or wheel positions.">Total</span>
        <span role="columnheader" className="pl-hide-m" title="What makes up Total: the shares, premium income (the option's time value earned), the cost of capped upside (the option's value above the strike) and dividends. The ticket splits premium income into time decay, volatility and price effect.">Total, explained</span>
        <span role="columnheader" className="pl-r" title="Theta per day: what the position earns per day from time decay if nothing else moves">θ / day</span>
        <span role="columnheader" className="pl-hide-m">Premium captured</span>
        <span role="columnheader" className="pl-hide-m">Δ entry → now</span>
        <span role="columnheader" />
      </div>
      {positions.map((p) => {
        const intent: Intent = p.intent ?? (p.keepSharesPreference === false ? 'release' : 'hold');
        return (
          <LedgerRow
            key={p.id}
            position={p}
            discipline={disciplines.get(p.id)}
            latest={latestPnl?.get(p.id)}
            deltaLine={thresholdsBundle?.[intent]?.defensive_delta ?? null}
            profitLine={thresholdsBundle?.[intent]?.profit_target_pct ?? null}
            open={openId === p.id}
            onToggle={toggle}
            onAction={onAction}
          />
        );
      })}
      {closedToday.length > 0 && (
        <>
          <div className="pl-group" role="row"><span role="cell">Ended today · counted in Today only</span></div>
          {closedToday.map((p) => <ClosedTodayRow key={p.id} position={p} />)}
        </>
      )}
      {openPos && (
        <PositionDrawer
          title={<>{openPos.ticker}<span className="pdr-strat">{openPos.positionType === 'cash_secured_put' ? 'Cash-secured put' : 'Covered call'}</span></>}
          subtitle={`${contractCode(openPos.expiryDate, openPos.strikePrice, openPos.positionType === 'cash_secured_put')} ×${openPos.numContracts || 1}`}
          price={<DrawerQuote position={openPos} latest={latestPnl?.get(openPos.id)} />}
          index={openIndex}
          count={positions.length}
          onPrev={() => openIndex > 0 && setOpenId(positions[openIndex - 1].id)}
          onNext={() => openIndex < positions.length - 1 && setOpenId(positions[openIndex + 1].id)}
          onClose={() => setOpenId(null)}
        >
          <PositionCockpit
            key={openPos.id}
            position={openPos}
            discipline={disciplines.get(openPos.id)}
            thresholdsBundle={thresholdsBundle}
            chartData={chartDataCache[openPos.ticker]}
            onAction={onAction}
            onDelete={(id) => { setOpenId(null); onDelete(id); }}
            onIntentChanged={onIntentChanged}
          />
        </PositionDrawer>
      )}
    </div>
  );
};

export default PositionLedger;
